import React, { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from "react";
import {
  useSaveSearchAnalyticsMutation,
  useSaveNoResultSearchAnalyticsMutation,
} from "Services/SearchAnalyticsApiSlice";

interface AnalyticsEntry {
  searchType: string;
  searchData: string;
  searchDateTime: string;
}

interface SearchAnalyticsContextType {
  queueAnalytics: (searchType: string, searchData: string) => void;
  resolveAnalytics: (hasResults: boolean) => void;
}

const SEARCH_LOGS_KEY = "sa_search_logs";
const NO_RESULT_LOGS_KEY = "sa_no_result_logs";
const FLUSH_INTERVAL_MS = 3_600_000; // 1 hour
// const FLUSH_INTERVAL_MS = 60_000; // 1 min

const SearchAnalyticsContext = createContext<SearchAnalyticsContextType | undefined>(undefined);

const getDateTime = (): string => new Date().toISOString().slice(0, 23);

const readLogs = (key: string): AnalyticsEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

const writeLogs = (key: string, logs: AnalyticsEntry[]): void => {
  localStorage.setItem(key, JSON.stringify(logs));
};

export const SearchAnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [saveSearchAnalytics] = useSaveSearchAnalyticsMutation();
  const [saveNoResultSearchAnalytics] = useSaveNoResultSearchAnalyticsMutation();

  const pendingRef = useRef<{ searchType: string; searchData: string }[]>([]);
  const flushRef = useRef<() => Promise<void>>();

  const flushAnalytics = useCallback(async () => {
    const searchLogs = readLogs(SEARCH_LOGS_KEY);
    const noResultLogs = readLogs(NO_RESULT_LOGS_KEY);

    if (searchLogs.length > 0) {
      try {
        await saveSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: searchLogs },
        }).unwrap();
        writeLogs(SEARCH_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }

    if (noResultLogs.length > 0) {
      try {
        await saveNoResultSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: noResultLogs },
        }).unwrap();
        writeLogs(NO_RESULT_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }
  }, [saveSearchAnalytics, saveNoResultSearchAnalytics]);

  useEffect(() => {
    flushRef.current = flushAnalytics;
  }, [flushAnalytics]);

  useEffect(() => {
    const interval = setInterval(() => {
      flushRef.current?.();
    }, FLUSH_INTERVAL_MS);

    // Flush immediately when connection is restored instead of waiting for the next tick
    const handleOnline = () => flushRef.current?.();
    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const queueAnalytics = useCallback((searchType: string, searchData: string) => {
    const trimmed = searchData.trim();
    if (!trimmed) return;
    pendingRef.current.push({ searchType, searchData: trimmed });
  }, []);

  const resolveAnalytics = useCallback((hasResults: boolean) => {
    const pending = pendingRef.current.splice(0);
    if (pending.length === 0) return;

    const now = getDateTime();
    const entries: AnalyticsEntry[] = pending.map((p) => ({
      searchType: p.searchType,
      searchData: p.searchData,
      searchDateTime: now,
    }));

    if (hasResults) {
      writeLogs(SEARCH_LOGS_KEY, [...readLogs(SEARCH_LOGS_KEY), ...entries]);
    } else {
      writeLogs(NO_RESULT_LOGS_KEY, [...readLogs(NO_RESULT_LOGS_KEY), ...entries]);
    }
  }, []);

  return (
    <SearchAnalyticsContext.Provider value={{ queueAnalytics, resolveAnalytics }}>
      {children}
    </SearchAnalyticsContext.Provider>
  );
};

export const useSearchAnalytics = (): SearchAnalyticsContextType => {
  const context = useContext(SearchAnalyticsContext);
  if (!context) throw new Error("useSearchAnalytics must be used within SearchAnalyticsProvider");
  return context;
};
